/**
 * DaxianTable：底部大限/流年/流月/流日/流时总览栏（固定 11 格布局）
 * 布局规范：
 * - 每行 11 个等宽等高格子：第 1 格为行标签，其余为内容格/箭头格/空位，占满整行无空白
 * - 大限/流月/流日/流时 内容超过 9 格时翻页：首页 [标签][9 内容][➡]；
 *   点击 ➡ 进入后续页（中间页 [标签][⬅][8 内容][➡]，末页 [标签][⬅][末 9 内容]）
 * - 流年行固定展示 11 格（标签 + 10 个年份，童限 1-N 岁为 N 格），不翻页
 * 交互（逐级下钻，再点取消）：
 * - 点击大限列 → 切大限层并展开该大限逐年流年；再点已选列 = 取消回本命
 * - 点击流年格子 → 切流年层；再点已选年 = 取消回大限
 * - 点击流月格子（需先选流年）→ 切流月层；再点已选月 = 取消回流年
 * - 点击流日格子（需先选流月）→ 切流日层；再点已选日 = 取消回流月
 * - 点击流时格子（需先选流日）→ 切流时层；再点已选时 = 取消回流日
 */
import { useEffect, useState, type ReactNode } from 'react';
import type { LiuriDay } from '../types/api';
import type { Base } from '../types/base';
import type { LiuyueMonth } from '../types/overlay';

interface Props {
  base: Base;
  /** 当前选中大限序号（表头高亮） */
  activeOrder?: number | '';
  /** 当前选中流年（流年行高亮） */
  activeYear?: number | '';
  /** 当前选中流月（lunarMonth 负数=闰月） */
  activeMonth?: number | '';
  /** 流月干支（实际农历月列表，长度 12 或 13，含 lunarMonth 字段） */
  liuyueMonths?: LiuyueMonth[];
  /** 当前排盘月候选日期（农历；liuri/days 结果） */
  liuriDays?: LiuriDay[];
  /** liuriDays 对应的农历年（点选流日时回填） */
  liuriLunarYear?: number;
  /** 当前选中流日（lunarMonth 负数 = 闰月） */
  activeDay?: { lunarMonth: number; lunarDay: number } | null;
  /** 流时 12 时辰干支（由 ChartPage 根据选中日干预计算） */
  liushiHours?: { label: string; gan: string; zhi: string }[];
  /** 当前选中流时辰序（0-11；空=未选） */
  activeShi?: number | '';
  /** 点击大限列回调（已选中时再次点击 = 取消） */
  onSelectOrder?: (order: number) => void;
  /** 点击流年回调（已选中时再次点击 = 取消） */
  onSelectYear?: (year: number) => void;
  /** 点击流月回调（已选中时再次点击 = 取消） */
  onSelectMonth?: (month: number) => void;
  /** 点击流日回调（已选中时再次点击 = 取消） */
  onSelectDay?: (d: { lunarYear: number; lunarMonth: number; lunarDay: number }) => void;
  /** 点击流时回调（已选中时再次点击 = 取消） */
  onSelectShi?: (shiIndex: number) => void;
}

const MONTH_CN = ['正月', '二月', '三月', '四月', '五月', '六月', '七月', '八月', '九月', '十月', '十一月', '腊月'];
const CN = ['', '一', '二', '三', '四', '五', '六', '七', '八', '九'];

/** 农历月名（含闰月前缀） */
function monthLabel(lunarMonth: number): string {
  const absM = Math.abs(lunarMonth);
  const base = MONTH_CN[absM - 1];
  return lunarMonth < 0 ? `闰${base}` : base;
}

/** 农历日中文（初一…廿九、三十） */
function lunarDayLabel(d: number): string {
  if (d === 10) return '初十';
  if (d === 20) return '二十';
  if (d === 30) return '三十';
  if (d < 10) return `初${CN[d]}`;
  if (d < 20) return `十${CN[d - 10]}`;
  return `廿${CN[d - 20]}`;
}

/** 固定格样式：等宽等高（flex-1 + h-6），手机窄格用 8px、≥sm 用 10px，两行内容不折行
 *  h-6（24px）为二次压缩行高（h-9→h-7→h-6）：两行 8px 文字（≈20px）仍装得下，
 *  再省 20px 纵向空间给命盘宫格（极端 6 个四化同宫徽标单列需行高 ≈114px） */
const CELL = 'flex h-6 items-center justify-center border-r border-slate-200 text-center text-[8px] sm:text-[10px]';

/**
 * 翻页窗口：首页 9 项，末页末 9 项，中间页 8 项（双箭头）；≤9 项不翻页。
 * 流月 12 个月：页1 = 1-9 + ➡，页2 = ⬅ + 4-12（末 9 项）
 */
function pageWindow<T>(items: T[], page: number): { shown: T[]; hasLeft: boolean; hasRight: boolean; page: number } {
  const n = items.length;
  if (n <= 9) return { shown: items, hasLeft: false, hasRight: false, page: 0 };
  const pageCount = 2 + Math.max(0, Math.ceil((n - 18) / 8));
  const p = Math.min(page, pageCount - 1);
  if (p === 0) return { shown: items.slice(0, 9), hasLeft: false, hasRight: true, page: p };
  if (p === pageCount - 1) return { shown: items.slice(n - 9), hasLeft: true, hasRight: false, page: p };
  const start = 9 + (p - 1) * 8;
  return { shown: items.slice(start, start + 8), hasLeft: true, hasRight: true, page: p };
}

/** 内容格按钮（两行文本，满格可点） */
function CellButton({
  active,
  activeClass,
  disabled,
  onClick,
  children,
}: {
  active?: boolean;
  activeClass?: string;
  disabled?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex h-full w-full flex-col items-center justify-center leading-snug transition-colors ${
        active ? activeClass : disabled ? 'cursor-not-allowed text-slate-300' : 'hover:opacity-75'
      }`}
    >
      {children}
    </button>
  );
}

/** 箭头格（翻页） */
function ArrowCell({ dir, onClick }: { dir: 'left' | 'right'; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-full w-full cursor-pointer items-center justify-center bg-slate-50 text-base text-slate-600 transition-colors hover:bg-slate-100"
    >
      {dir === 'left' ? '⬅' : '➡'}
    </button>
  );
}

/** 固定 11 格行：标签格 + 至多 10 个内容格（不足补空位，占满整行） */
function SlotRow({ label, labelClass, cells }: { label: string; labelClass: string; cells: (ReactNode | null)[] }) {
  const slots: ReactNode[] = [];
  for (let i = 0; i < 10; i++) slots.push(cells[i] ?? <div className="h-full w-full" />);
  return (
    <div className="flex border-b border-slate-200 last:border-b-0">
      <div className={`${CELL} ${labelClass} flex-1`}>{label}</div>
      {slots.map((c, i) => (
        <div key={i} className={`${CELL} flex-1`}>
          {c}
        </div>
      ))}
    </div>
  );
}

/** 空状态行：标签格固定 1/11 跨度（与内容行对齐），提示横跨剩余 10/11（字号与标签一致 text-[8px] sm:text-[10px]） */
function HintRow({ label, labelClass, hint }: { label: string; labelClass: string; hint: string }) {
  return (
    <div className="flex border-b border-slate-200 last:border-b-0">
      <div className={`${CELL} ${labelClass} flex-1`}>{label}</div>
      <div className="flex h-6 flex-[10] items-center justify-center text-[8px] text-slate-400 sm:text-[10px]">{hint}</div>
    </div>
  );
}

export default function DaxianTable({
  base,
  activeOrder = '',
  activeYear = '',
  activeMonth = '',
  liuyueMonths,
  liuriDays = [],
  liuriLunarYear,
  activeDay = null,
  liushiHours = [],
  activeShi = '',
  onSelectOrder,
  onSelectYear,
  onSelectMonth,
  onSelectDay,
  onSelectShi,
}: Props) {
  const renshiByZhi = new Map(base.benming.map((p) => [p.zhi, p.renshigong]));
  const daxian = [...base.daxianSummary].sort((a, b) => a.order - b.order);
  const selected = daxian.find((d) => d.order === activeOrder);

  /** 童限虚岁区间：1 ~ 起运前一年（速查表 31 节：起运前走童限，六岁/起运后入大限） */
  const tongxianMaxAge = Math.max(1, (base.meta.qiyun ?? 5) - 1);

  /** 选中大限的逐年列表：{year, age虚岁}；activeOrder=0 时列童限逐年（一命二财三疾厄…） */
  const birthYear = base.meta.solar[0];
  const years =
    activeOrder === 0
      ? (() => {
          const list: { year: number; age: number }[] = [];
          for (let age = 1; age <= tongxianMaxAge; age++) list.push({ year: birthYear + age - 1, age });
          return list;
        })()
      : selected && /^\d{4}-\d{4}$/.test(selected.yearRange)
        ? (() => {
            const [s, e] = selected.yearRange.split('-').map(Number);
            const list: { year: number; age: number }[] = [];
            for (let y = s; y <= e; y++) list.push({ year: y, age: y - birthYear + 1 });
            return list;
          })()
        : [];

  // ---- 翻页状态（行内容变化时重置回首页） ----
  const [daxianPage, setDaxianPage] = useState(0);
  const [liuyuePage, setLiuyuePage] = useState(0);
  const [liuriPage, setLiuriPage] = useState(0);
  const [liushiPage, setLiushiPage] = useState(0);
  useEffect(() => setDaxianPage(0), [base]);
  useEffect(() => setLiuyuePage(0), [liuyueMonths]);
  useEffect(() => setLiuriPage(0), [liuriDays]);
  useEffect(() => setLiushiPage(0), [liushiHours]);

  // ---- 大限行（起限前 + 12 大限） ----
  const daxianCells: (ReactNode | null)[] = [];
  {
    const items: { key: string; node: ReactNode }[] = [
      {
        key: 'tongxian',
        node: (
          <CellButton
            active={activeOrder === 0}
            activeClass="bg-green-600 font-semibold text-white"
            onClick={() => onSelectOrder?.(0)}
          >
            <span className="block leading-tight">起限前</span>
            <span className="block leading-tight opacity-80">(1-{tongxianMaxAge}岁)</span>
          </CellButton>
        ),
      },
      ...daxian.map((d) => {
        const active = d.order === activeOrder;
        return {
          key: d.zhi,
          node: (
            <CellButton
              active={active}
              activeClass="bg-green-600 font-semibold text-white"
              onClick={() => onSelectOrder?.(d.order)}
            >
              <span className="block leading-tight">{d.ageRange}</span>
              <span className={`block leading-tight ${active ? 'text-green-100' : 'opacity-80'}`}>
                {renshiByZhi.get(d.zhi)}
              </span>
            </CellButton>
          ),
        };
      }),
    ];
    const w = pageWindow(items, daxianPage);
    if (w.hasLeft) daxianCells.push(<ArrowCell key="l" dir="left" onClick={() => setDaxianPage(w.page - 1)} />);
    w.shown.forEach((it) => daxianCells.push(it.node));
    if (w.hasRight) daxianCells.push(<ArrowCell key="r" dir="right" onClick={() => setDaxianPage(w.page + 1)} />);
  }

  // ---- 流年行（标签 + 10 年份，不翻页） ----
  const yearCells: (ReactNode | null)[] = years.map((y) => {
    const active = y.year === activeYear;
    return (
      <CellButton
        key={y.year}
        active={active}
        activeClass="bg-blue-500 font-medium text-white"
        disabled={!onSelectYear}
        onClick={() => onSelectYear?.(y.year)}
      >
        <span className={`block leading-tight ${active ? 'text-white' : 'font-medium text-slate-700'}`}>{y.year}年</span>
        <span className={`block leading-tight ${active ? 'text-blue-100' : 'text-slate-400'}`}>{y.age}岁</span>
      </CellButton>
    );
  });

  // ---- 流月行（起限前=order 0 不在 daxianSummary 中，与流年行同款用 activeOrder===0 放行） ----
  const liuyueCells: (ReactNode | null)[] = [];
  {
    const items = ((selected || activeOrder === 0) ? liuyueMonths ?? [] : []).map((m) => {
      const active = m.lunarMonth === activeMonth;
      const clickable = !!onSelectMonth && activeYear !== '';
      return {
        key: m.lunarMonth,
        node: (
          <CellButton
            active={active}
            activeClass="bg-amber-500 font-medium text-white"
            disabled={!clickable}
            onClick={() => onSelectMonth?.(m.lunarMonth)}
          >
            <span className="block leading-tight">{monthLabel(m.lunarMonth)}</span>
            <span className={`block leading-tight ${active ? 'text-amber-100' : 'opacity-75'}`}>
              {m.gan}
              {m.zhi}
            </span>
          </CellButton>
        ),
      };
    });
    const w = pageWindow(items, liuyuePage);
    if (w.hasLeft) liuyueCells.push(<ArrowCell key="l" dir="left" onClick={() => setLiuyuePage(w.page - 1)} />);
    w.shown.forEach((it) => liuyueCells.push(it.node));
    if (w.hasRight) liuyueCells.push(<ArrowCell key="r" dir="right" onClick={() => setLiuyuePage(w.page + 1)} />);
  }

  // ---- 流日行 ----
  const liuriCells: (ReactNode | null)[] = [];
  {
    const items = (activeMonth !== '' ? liuriDays : []).map((d) => {
      const active = activeDay?.lunarMonth === d.lunarMonth && activeDay?.lunarDay === d.lunarDay;
      return {
        key: `${d.lunarMonth}-${d.lunarDay}`,
        node: (
          <CellButton
            active={active}
            activeClass="bg-purple-500 font-medium text-white"
            disabled={!onSelectDay || liuriLunarYear === undefined}
            onClick={() => onSelectDay?.({ lunarYear: liuriLunarYear!, lunarMonth: d.lunarMonth, lunarDay: d.lunarDay })}
          >
            <span className="block leading-tight">{lunarDayLabel(d.lunarDay)}</span>
            <span className={`block leading-tight ${active ? 'text-purple-100' : 'opacity-75'}`}>
              {d.gan}
              {d.zhi}
            </span>
          </CellButton>
        ),
      };
    });
    const w = pageWindow(items, liuriPage);
    if (w.hasLeft) liuriCells.push(<ArrowCell key="l" dir="left" onClick={() => setLiuriPage(w.page - 1)} />);
    w.shown.forEach((it) => liuriCells.push(it.node));
    if (w.hasRight) liuriCells.push(<ArrowCell key="r" dir="right" onClick={() => setLiuriPage(w.page + 1)} />);
  }

  // ---- 流时行 ----
  const liushiCells: (ReactNode | null)[] = [];
  {
    const items = (activeDay !== null && liushiHours.length === 12 ? liushiHours : []).map((h, i) => {
      const active = i === activeShi;
      return {
        key: i,
        node: (
          <CellButton
            active={active}
            activeClass="bg-slate-700 font-medium text-white"
            disabled={!onSelectShi}
            onClick={() => onSelectShi?.(i)}
          >
            <span className="block leading-tight">{h.label}</span>
            <span className={`block leading-tight ${active ? 'text-slate-100' : 'opacity-75'}`}>
              {h.gan}
              {h.zhi}
            </span>
          </CellButton>
        ),
      };
    });
    const w = pageWindow(items, liushiPage);
    if (w.hasLeft) liushiCells.push(<ArrowCell key="l" dir="left" onClick={() => setLiushiPage(w.page - 1)} />);
    w.shown.forEach((it) => liushiCells.push(it.node));
    if (w.hasRight) liushiCells.push(<ArrowCell key="r" dir="right" onClick={() => setLiushiPage(w.page + 1)} />);
  }

  return (
    <div className="w-full max-w-[40rem] rounded-lg bg-white shadow max-sm:-mx-4 max-sm:min-w-[calc(100%+2rem)] sm:mx-auto">
      <SlotRow label="大限" labelClass="bg-slate-100 font-medium text-slate-700" cells={daxianCells} />
      {years.length > 0 ? (
        <SlotRow label="流年" labelClass="bg-blue-50 font-medium text-blue-700" cells={yearCells} />
      ) : (
        <HintRow label="流年" labelClass="bg-blue-50 font-medium text-blue-700" hint="请选择大限或起限前" />
      )}
      {liuyueCells.length > 0 ? (
        <SlotRow label="流月" labelClass="bg-amber-50 font-medium text-amber-700" cells={liuyueCells} />
      ) : (
        <HintRow label="流月" labelClass="bg-amber-50 font-medium text-amber-700" hint="请选择大限或起限前" />
      )}
      {liuriCells.length > 0 ? (
        <SlotRow label="流日" labelClass="bg-purple-50 font-medium text-purple-700" cells={liuriCells} />
      ) : (
        <HintRow label="流日" labelClass="bg-purple-50 font-medium text-purple-700" hint="请选择流月" />
      )}
      {liushiCells.length > 0 ? (
        <SlotRow label="流时" labelClass="bg-slate-100 font-medium text-slate-700" cells={liushiCells} />
      ) : (
        <HintRow label="流时" labelClass="bg-slate-100 font-medium text-slate-700" hint="请选择流日" />
      )}
    </div>
  );
}
